import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import Login from './Login';
import Signup from './Signup';

/* ── Utility: decode JWT payload (no verify, client-only) ── */
const decodeJwt = (token) => {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

/* ── Is token expired? ───────────────────────────────────── */
const isTokenExpired = (token) => {
  const decoded = decodeJwt(token);
  if (!decoded?.exp) return true;
  return Date.now() >= decoded.exp * 1000;
};

/* ── Loading splash ──────────────────────────────────────── */
const LoadingScreen = () => (
  <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    gap: 2,
    background: 'linear-gradient(145deg, #060d1f 0%, #0B132B 45%, #111e3d 100%)',
  }}>
    <Box component="img" src={`${process.env.PUBLIC_URL || ''}/iso.png`} alt="Absolluty" sx={{ width: 56, height: 56, objectFit: 'contain' }} />
    <CircularProgress size={28} sx={{ color: '#6FFFE9' }} />
    <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', letterSpacing: '2px' }}>
      VERIFICANDO SESIÓN...
    </Typography>
  </Box>
);

/* ══════════════════════════════════════════════════════════ */
/*  AUTH WRAPPER                                              */
/* ══════════════════════════════════════════════════════════ */
const AuthWrapper = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState('viewer');
  const [showLogin, setShowLogin] = useState(true);
  const [loading, setLoading] = useState(true);

  /* ── Rehydrate session from localStorage ─────────────── */
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const roleData = localStorage.getItem('role');

    if (token && userData) {
      /* Reject expired tokens immediately */
      if (isTokenExpired(token)) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('role');
        setLoading(false);
        return;
      }
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setRole(roleData || 'viewer');
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('role');
      }
    }
    setLoading(false);
  }, []);

  /* ── Handlers ────────────────────────────────────────── */
  const handleLogin = useCallback((userData, token) => {
    const storedRole = localStorage.getItem('role') || 'viewer';
    setUser(userData);
    setRole(storedRole);
    setIsAuthenticated(true);
  }, []);

  const handleSignup = useCallback(() => {
    setShowLogin(true);
  }, []);

  const handleLogout = useCallback(() => {
    const token = localStorage.getItem('token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('role');
    setUser(null);
    setRole('viewer');
    setIsAuthenticated(false);

    /* Fire-and-forget logout on server */
    if (token) {
      fetch('/v1/api/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => { });
    }
  }, []);

  /* ── Render ──────────────────────────────────────────── */
  if (loading) return <LoadingScreen />;

  if (!isAuthenticated) {
    return showLogin ? (
      <Login onLogin={handleLogin} switchToSignup={() => setShowLogin(false)} />
    ) : (
      <Signup onSignup={handleSignup} switchToLogin={() => setShowLogin(true)} />
    );
  }

  /* Pass user, role and logout to the wrapped app */
  return React.cloneElement(children, { user, role, onLogout: handleLogout });
};

export default AuthWrapper;
